import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Permissions from '../model/Permissions'
import { AppThunk } from 'store'
import { userRoles, userRolesBase } from 'model/User'

interface UserState {
  permissions: Permissions[]
}

export const allNotAdminPermissions = [
  Permissions.ReadPatients,
  Permissions.WritePatients,

  Permissions.ReadAppointments,
  Permissions.WriteAppointments,

  Permissions.ReadAllergy,
  Permissions.WriteAllergy,

  Permissions.ReadMedication,
  Permissions.WriteMedication,

  Permissions.ReadDiagnosis,
  Permissions.WriteDiagnosis,

  Permissions.RequestLab,
  Permissions.CancelLab,
  Permissions.CompleteLab,
  Permissions.ViewLab,
  Permissions.ViewLabs,

  Permissions.ReadSettings,
  Permissions.WriteSettings,

  Permissions.ReadClinics,
  Permissions.WriteClinics,
  Permissions.addClinics,

  Permissions.ReadNotes,
  Permissions.WriteNotes,

  Permissions.ReadNutrition,
  Permissions.WriteNutrition,

  Permissions.ReadPhysicalActivity,
  Permissions.WritePhysicalActivity,

  Permissions.ReadReferral,
  Permissions.WriteReferral,

  Permissions.ReadPatientAppointmentTab,
  Permissions.WritePatientAppointmentTab,

  Permissions.ReadPatientDashboardTab,
  Permissions.WritePatientDashboardTab,

  Permissions.ReadPatientMedicalFileTab,
  Permissions.WritePatientMedicalFileTab,

  Permissions.ReadExaminationsList,
  Permissions.WriteExaminationsList,

  Permissions.ReadMonitoring,
  Permissions.WriteMonitoring,

  Permissions.ReadLabTestsList,
  Permissions.WriteLabTestsList,
]

const initialState: UserState = {
  permissions: [],
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    fetchPermissions(state, { payload }: PayloadAction<Permissions[]>) {
      state.permissions = payload
    },
  },
})

export const { fetchPermissions } = userSlice.actions

export const grantAccess = (): AppThunk => async dispatch => {
  dispatch(fetchPermissions([]))
}

export const denyAccess = (): AppThunk => async dispatch => {
  dispatch(fetchPermissions([]))
}

export const fetchPermissionsForUser = (
  role: userRolesBase,
  admin: boolean = false,
): AppThunk => async dispatch => {
  let permissions: Permissions[] = []

  // A supply manager has no clinical access at all — they count boxes, they do not open charts.
  // Their two screens are gated by canManageSupplyChecklist / canViewSupplyReports in
  // utils/enums.ts rather than by a permission, so the empty list is the whole story. It has to
  // be checked before the catch-all below, which would otherwise hand them
  // allNotAdminPermissions along with every other role.
  if (role === userRolesBase.supplyManager) {
    if (admin) permissions.push(Permissions.AdminPermissions)
    dispatch(fetchPermissions(permissions))
    return
  }

  // An interpreter reads studies: they browse the patient file around the result they were sent
  // to, and write only the interpretation (which the exam modal gates on the role, not on a
  // permission). Nothing here lets them order, chart or edit the patient.
  if (role === userRolesBase.interpreter) {
    permissions.push(
      Permissions.ReadPatients,
      // The workspace, which is where a worklist row opens: the patient summary and their exams.
      Permissions.ReadPatientAppointmentTab,
      Permissions.ReadPatientMedicalFileTab,
      Permissions.ReadPatientDashboardTab,
      Permissions.ReadExaminationsList,
      Permissions.ReadLabTestsList,
      Permissions.ReadMonitoring,
      Permissions.ReadDiagnosis,
      Permissions.ReadAllergy,
      Permissions.ReadMedication,
    )
    if (admin) permissions.push(Permissions.AdminPermissions)
    dispatch(fetchPermissions(permissions))
    return
  }

  if (role === userRolesBase.manager || role === userRolesBase.medTech) {
    permissions.push(
      Permissions.ReadPatients,
      Permissions.WritePatients,

      Permissions.ReadPatientMedicalFileTab,
      Permissions.WritePatientMedicalFileTab,

      Permissions.addClinics,

      Permissions.ReadExaminationsList,
      Permissions.ReadLabTestsList,
    )
    if (role === userRolesBase.medTech) {
      permissions.push(
        Permissions.ReadPatientAppointmentTab,
        Permissions.WriteprintBraclet,
        Permissions.ReadMonitoring,
        Permissions.ReadPatientDashboardTab,
        Permissions.ReadDiagnosis,
        Permissions.ReadAllergy,
        Permissions.ReadMedication,
      )
    }
  } else if (userRoles.includes(role)) permissions.push(...allNotAdminPermissions)
  if (role === userRolesBase.doctor) permissions.push(Permissions.assignOwner)
  if (admin) permissions.push(Permissions.AdminPermissions)

  dispatch(fetchPermissions(permissions))
}

export default userSlice.reducer
